import React, { Fragment } from 'react'
import { graphql } from 'gatsby'
import styled from '@emotion/styled'
import { keyframes } from '@emotion/core'
import Img from 'gatsby-image'
import { Parallax as ParallaxObject } from 'react-scroll-parallax'

import { getProject } from '../../projects'
import {
  ProjectLayout,
  ProjectIntro,
  Title,
  Paragraph,
  dropShadow,
} from '../../layouts/project'

import { useNavbarTheme } from '../../contexts/NavbarTheme'

import theme from '../../styles/theme'
import { media, rem, selection, lines } from '../../styles/tools'

import { AutoFade, AutoSlide } from '../../components/Animated'
import Wrapper, { wrapperPadding } from '../../components/Wrapper'
import Space from '../../components/Space'
import Parallax from '../../components/Parallax'
import BackgroundText from '../../components/BackgroundText'
import Link from '../../components/Link'
import { Headline, BoldText } from '../../styles/typography'

import Logo from '../../assets/pages/work/tennis-channel/Logo.js'

export const query = graphql`
  {
    headerImage: file(relativePath: { eq: "pages/work/tennis-channel.jpg" }) {
      ...windowWide
    }
    facebookImage: file(relativePath: { eq: "pages/work/tennis-channel.jpg" }) {
      ...facebookShareImage
    }
    twitterImage: file(relativePath: { eq: "pages/work/tennis-channel.jpg" }) {
      ...twitterShareImage
    }
    desktopAndPhone: file(
      relativePath: { eq: "pages/work/tennis-channel/desktop-and-phone.png" }
    ) {
      ...smallImage
    }
    homepage: file(
      relativePath: { eq: "pages/work/tennis-channel/homepage.png" }
    ) {
      ...smallImage
    }
    ipad: file(relativePath: { eq: "pages/work/tennis-channel/ipad.png" }) {
      ...smallImage
    }
    sitemap: file(
      relativePath: { eq: "pages/work/tennis-channel/sitemap.png" }
    ) {
      ...smallImage
    }
    webpageAfter: file(
      relativePath: { eq: "pages/work/tennis-channel/webpage-after.png" }
    ) {
      ...smallImage
    }
    webpageBefore: file(
      relativePath: { eq: "pages/work/tennis-channel/webpage-before.png" }
    ) {
      ...smallImage
    }
    wireframe: file(
      relativePath: { eq: "pages/work/tennis-channel/wireframe.png" }
    ) {
      ...smallImage
    }
    tennisBall: file(
      relativePath: { eq: "pages/work/tennis-channel/tennis-ball.png" }
    ) {
      ...smallImage
    }
  }
`

let project = getProject('tennis-channel')

export default ({ data }) => {
  project = {
    ...project,
    headerImage: data.headerImage.childImageSharp.fluid,
    facebookImage: data.facebookImage.childImageSharp.fixed.src,
    twitterImage: data.twitterImage.childImageSharp.fixed.src,
    logo: <Logo />,
  }

  const desktopAndPhone = data.desktopAndPhone.childImageSharp.fluid
  const homepage = data.homepage.childImageSharp.fluid
  const ipad = data.ipad.childImageSharp.fluid
  const sitemap = data.sitemap.childImageSharp.fluid
  const webpageAfter = data.webpageAfter.childImageSharp.fluid
  const webpageBefore = data.webpageBefore.childImageSharp.fluid
  const wireframe = data.wireframe.childImageSharp.fluid
  const tennisBall = data.tennisBall.childImageSharp.fluid

  useNavbarTheme('dark')

  return (
    <ProjectLayout project={project}>
      <ProjectIntro
        project={project}
        text={
          <Fragment>
            <AutoFade>
              <Title as="h1" css={{ maxWidth: '11em' }}>
                Smashing the old, serving up a new look.
              </Title>
            </AutoFade>
            <Space y={32} />
            <AutoFade>
              <Paragraph>
                We served up Tennis Channel’s website with a much-needed update
                — a responsive web experience that would look great on any
                device and bring their dynamic video content to life to create
                an immersive digital fan experience.
              </Paragraph>
            </AutoFade>
          </Fragment>
        }
      >
        <Space y={{ mobile: 60, tablet: 100 }} />
        <AutoSlide up css={{ marginBottom: '-40vmin' }}>
          <Img fluid={desktopAndPhone} />
        </AutoSlide>
      </ProjectIntro>
      <Wrapper padded css={{ minHeight: '100vh' }}>
        <Space y="40vmin" />
        Content
      </Wrapper>
      <Parallax
        direction="both"
        distance={0.3}
        background={
          <div
            css={[
              lines('rgba(255, 255, 255, .06)'),
              {
                display: 'flex',
                alignItems: 'center',
                height: '130%',
                userSelect: 'none',
                backgroundColor: project.color,
              },
            ]}
          >
            <svg
              viewBox="0 18 65.8 141.8"
              fill="#00654f"
              css={{
                width: '100%',
                marginTop: '70%',
                [media.tabletLg]: {
                  width: '60%',
                  marginTop: '50%',
                },
              }}
            >
              <path d="M40.4,49.4l18.8,2.5l2,0.3v33h-31l0,0l0,0L40.4,49.4z" />
              <path d="M4.6,44.8l21.7,2.8l2.1,0.3L17.8,85.2H4.6V44.8z" />
              <path d="M61.2,28.6v11.5L4.6,32.7v-4.1H61.2z" />
            </svg>
          </div>
        }
      >
        <div
          css={[
            wrapperPadding,
            selection(theme.colors.white, project.color),
            {
              display: 'flex',
              alignItems: 'center',
              height: '65vh',
              maxHeight: rem(600),
              color: theme.colors.white,
            },
          ]}
        >
          <AutoSlide up translate={50}>
            <Title
              as="blockquote"
              css={{ maxWidth: '18em', textAlign: 'center' }}
            >
              Our responsive design provided a content-rich viewing experience
              for Tennis fans everywhere.
            </Title>
          </AutoSlide>
        </div>
      </Parallax>
    </ProjectLayout>
  )
}
